import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiGet, apiPost } from "../api/client";

interface TournamentRefreshStatus {
  syncing: boolean;
  syncError?: string;
  lastSyncedAt?: string;
}

function delay(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function refreshTournament(tournamentId: string) {
  await apiPost<{ accepted: boolean }>(`/api/v1/tournaments/${tournamentId}/refresh`);
  for (let attempt = 0; attempt < 150; attempt += 1) {
    await delay(attempt === 0 ? 400 : 2_000);
    const status = await apiGet<TournamentRefreshStatus>(`/api/v1/tournaments/${tournamentId}/sync-status`);
    if (status.syncing) continue;
    if (status.syncError) throw new Error(status.syncError);
    return status;
  }
  throw new Error("Tournament refresh timed out");
}

export function useTournamentRefresh(tournamentId: string) {
  const queryClient = useQueryClient();
  const [wiki, slug] = tournamentId.split("/");
  return useMutation({
    mutationFn: () => refreshTournament(tournamentId),
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ["feed"] }),
      queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
      queryClient.invalidateQueries({ queryKey: ["tournament", wiki, slug] }),
    ]),
  });
}
